import { useEffect, useState } from "react";

import { type Conversation } from "shared/apiClient";
import { getText, interpolate } from "shared/lib";

import { useCouncilLocalisation, useI18nReady } from "shared/hooks";

import { Checkbox } from "shared/components";

export interface AnalysisDisclaimerContentProps {
  conversation: Conversation | undefined | null;
  onChangeDisclaimerAcceptance: (accepted: boolean) => void;
}

export function AnalysisDisclaimerContent({
  conversation,
  onChangeDisclaimerAcceptance,
}: AnalysisDisclaimerContentProps) {
  const [checked, setChecked] = useState(false);
  const isI18nReady = useI18nReady();

  const { getLocalisationEntry } = useCouncilLocalisation(conversation);

  useEffect(() => {
    onChangeDisclaimerAcceptance(checked);
  }, [checked, onChangeDisclaimerAcceptance]);

  // Wait for i18n to be ready before rendering interpolated content
  if (!isI18nReady) {
    return (
      <div className="flex flex-col gap-3">
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <h3 className="text-textPrimary text-headingmd">
        {getText(
          "The analysis will provide a view regarding whether or not your proposal may require a planning permit, as well as an indication of applicable planning requirements.",
        )}
      </h3>

      <div className="inline-flex gap-2">
        <Checkbox
          className="border-interactiveBorder rounded-sm"
          data-testid="confirm-report-checkbox"
          id="preliminary-information-only"
          onCheckedChange={(checked) => setChecked(!!checked)}
          name="preliminary-information-only"
          role="checkbox"
        />
        <div className="ml-3">
          <label
            className="text-textPrimary text-bodymd"
            htmlFor="preliminary-information-only"
          >
            <p>
              {interpolate(
                "I agree to the terms outlined in the {{link}}, notably:",
                {
                  link: (
                    <a
                      className="text-textLink cursor-pointer underline"
                      href="/end-user-agreement"
                      rel="noreferrer"
                      target="_blank"
                      tabIndex={0}
                    >
                      {getText("End User Agreement")}
                    </a>
                  ),
                },
              )}
            </p>
            <ul className="mt-1 ml-5 list-outside list-disc [&>li:not(:last-child)]:mb-1">
              <li>
                {interpolate(
                  "a record of your enquiry and its analysis will be shared with and retained by {{councilName}}",
                  {
                    councilName: getLocalisationEntry("councilName"),
                  },
                )}
              </li>
              <li>
                {getText(
                  "the analysis is a guide only and does not represent an official planning decision",
                )}
              </li>
              <li>
                {interpolate(
                  "there are {{link}} that the analysis doesn't take into account.",
                  {
                    link: (
                      <a
                        className="text-textLink cursor-pointer underline"
                        href={`/analysis-exclusions?state=${conversation?.address_data?.state}`}
                        rel="noreferrer"
                        target="_blank"
                        tabIndex={0}
                      >
                        {getText("several variables.")}
                      </a>
                    ),
                  },
                )}
              </li>
              <li>
                {interpolate(
                  "{{link}} (the company that provides this service to your council) and your council do not provide any warranties, express or implied, in relation to the analysis.",
                  {
                    link: (
                      <a
                        className="text-textLink cursor-pointer underline"
                        href="https://mylot.ai"
                        rel="noreferrer"
                        target="_blank"
                        tabIndex={0}
                      >
                        {getText("myLot")}
                      </a>
                    ),
                  },
                )}
              </li>
            </ul>
          </label>
        </div>
      </div>
    </div>
  );
}
